import { formatNumber } from "sipli-fleet-core/utils/formatNumber";
import i18n from "src/translationService";
import { Alert, Spin } from "antd";
import type { ComponentType, SVGProps } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CustomButton, CustomTypography } from "sipli-fleet-core/components";
import { BACK_ICON } from "src/assets/svgs";
import { useQueryGetRoutePresetById } from "src/queries/routePresets.queries";
import { useQueryGetVehicleById } from "src/queries/unitManagement.queries";
import type { RoutePresetRecord } from "src/services/routePresetsService";
import CustomerBreadcrumb from "../CustomerBreadcrumb";

type DetailRecord = Record<string, unknown>;

const BackIcon = BACK_ICON as unknown as ComponentType<SVGProps<SVGSVGElement>>;

const asRecord = (value: unknown): DetailRecord =>
  value && typeof value === "object" ? (value as DetailRecord) : {};

const readFirstValue = (records: DetailRecord[], keys: string[]) => {
  for (const record of records) {
    for (const key of keys) {
      const value = record[key];
      if (value !== undefined && value !== null && value !== "") return value;
    }
  }

  return undefined;
};

const getRoutePresetDetailRecord = (
  response?: RoutePresetRecord | RoutePresetRecord[] | null,
  presetId?: string
) => {
  if (Array.isArray(response)) {
    return (
      response.find((record) => String(record.id) === String(presetId)) ??
      response.slice().reverse().find(Boolean) ??
      null
    );
  }

  return response ?? null;
};

const formatEnum = (value?: string | null) => {
  if (!value) return "--";
  return value.replace(/_/g, " ");
};

const formatPresetValue = (value: unknown, suffix = "") => {
  if (value === null || value === undefined || value === "") return "--";
  return `${typeof value === "number" || typeof value === "string" ? formatNumber(value, i18n.language) : "--"}${suffix}`;
};

const getCriteriaLabel = (record?: RoutePresetRecord | null) => {
  if (!record) return "--";
  if (record.routeMode === "MANAGED_BY_IGNITION") return formatEnum(record.startStopCriteria);
  return "Only by criteria";
};

const DetailItem = ({ label, value }: { label: string; value: unknown }) => (
  <div className="rounded-[8px] border border-gray3 bg-white px-4 py-3">
    <CustomTypography.CustomText className="!block !text-[12.44px] !font-medium !leading-[16px] !text-gray8">
      {label}
    </CustomTypography.CustomText>
    <CustomTypography.CustomText className="!mt-1 !block !text-sm !font-semibold !leading-[18px] !text-black">
      {value === null || value === undefined || value === "" ? "--" : String(value)}
    </CustomTypography.CustomText>
  </div>
);

const RoutePresetDetailPage = () => {
  const { clientId, unitId, vehicleId, presetId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const detailQuery = useQueryGetRoutePresetById(presetId, { enabled: !!presetId });
  const { data: vehicleDetail } = useQueryGetVehicleById(vehicleId, { enabled: !!vehicleId });

  const preset = useMemo(
    () => getRoutePresetDetailRecord(detailQuery.data?.data.response, presetId),
    [detailQuery.data?.data.response, presetId]
  );

  const vehicleRecord = asRecord(vehicleDetail);
  const vehicleName = String(
    readFirstValue([vehicleRecord], ["vehicleName", "name", "label", "model"]) ??
      `${t("vehicle")} ${vehicleId ?? ""}`.trim()
  );
  const vehiclePath = `/customers/${encodeURIComponent(clientId ?? "")}/${encodeURIComponent(
    unitId ?? ""
  )}/${encodeURIComponent(vehicleId ?? "")}?tab=routes`;

  return (
    <section className="min-h-full bg-gray1 px-6 py-7">
      <div className="mb-7">
        <div className="customer-detail-header">
          <div className="flex items-center gap-3">
            <BackIcon
              className="w-[16px] cursor-pointer"
              onClick={() => navigate(vehiclePath, { state })}
              aria-label="Back to route presets"
            />
            <CustomTypography.CustomTitle
              level={3}
              className="customer-detail-title !m-0 !text-[25.23px] !font-bold"
            >
              {t("route_presets_details")}
            </CustomTypography.CustomTitle>
          </div>
          <div className="min-w-0 col-start-2">
            <CustomerBreadcrumb
              ariaLabel={t("route_presets_details")}
              separator="/"
              items={[
                {
                  label: t("customers"),
                  path: "/customers",
                },
                {
                  label: t("vehicle_list"),
                  path: `/customers/${encodeURIComponent(clientId ?? "")}?tab=devices`,
                  state,
                },
                {
                  label: vehicleName,
                  path: vehiclePath,
                  state,
                },
                {
                  label: t("route_presets"),
                  path: vehiclePath,
                  state,
                },
                {
                  label: preset?.name ?? t("route_presets_details"),
                },
              ]}
            />
          </div>
        </div>
      </div>

      {detailQuery.isError && (
        <Alert className="mb-5" type="error" message={t("route_preset_detail_load_failed")} showIcon />
      )}

      <Spin spinning={detailQuery.isLoading || detailQuery.isFetching}>
        <div className="rounded-[16px] border border-gray3 bg-white p-5">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <CustomTypography.CustomText className="!block !text-[19.93px] !font-bold !leading-[28px] !text-black">
                {preset?.name ?? t("route_presets_details")}
              </CustomTypography.CustomText>
              <CustomTypography.CustomText className="!mt-1 !block !text-sm !leading-[22px] !text-gray9">
                {t("route_presets")}
              </CustomTypography.CustomText>
            </div>
            <CustomButton
              type="secondary"
              label={t("back", { defaultValue: "Back" })}
              onClick={() => navigate(vehiclePath, { state })}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            <DetailItem label={t("name")} value={preset?.name} />
            <DetailItem label={t("base_presets")} value={preset?.isBase ? t("base_presets") : "--"} />
            <DetailItem label={t("routeMode")} value={formatEnum(preset?.routeMode)} />
            <DetailItem label={t("startStopCriteria")} value={getCriteriaLabel(preset)} />
            <DetailItem label={t("detectionBasedOn")} value={formatEnum(preset?.detectionBasedOn)} />
            {preset?.detectionBasedOn === "SPEED" && (
              <DetailItem
                label={t("minimumSpeedToStartRoute")}
                value={formatPresetValue(preset?.minSpeedKmh)}
              />
            )}
            {preset?.detectionBasedOn === "GPS_COORDS" && (
              <DetailItem label={t("geofenceInM")} value={formatPresetValue(preset?.geofenceMeters)} />
            )}
            <DetailItem
              label={t("validationPointToStartRoute")}
              value={formatPresetValue(preset?.validationPoints)}
            />
            <DetailItem label={t("timeOutInIdleStateInSec")} value={formatPresetValue(preset?.idleTimeoutSec)} />
            <DetailItem
              label={t("timeOutWithoutDataInSec")}
              value={formatPresetValue(preset?.noDataTimeoutSec)}
            />
            <DetailItem
              label={t("maxSpeed")}
              value={formatPresetValue(preset?.maxSpeedKmh, preset?.maxSpeedKmh ? " km/h" : "")}
            />
            <DetailItem
              label={t("timeOutToNoDataState")}
              value={
                preset?.timeoutNoData
                  ? formatPresetValue(Number(preset.timeoutNoData) / 60)
                  : "--"
              }
            />
          </div>
        </div>
      </Spin>
    </section>
  );
};

export default RoutePresetDetailPage;
